import { schema as s } from 'normalizr';

import Logger from '../lib/Logger';

const country = new s.Entity('countries');
const region = new s.Entity('regions', {
  country: country,
});
const subregion = new s.Entity('subregions', {
  region: region,
});
const city = new s.Entity('cities', {
  region: region,
  subregion: subregion,
});
const postalCode = new s.Entity('postalCodes', {
  region: region,
  subregion: subregion,
  city: city,
});
const role = new s.Entity('roles');
const termsOfService = new s.Entity('termsOfServices');
const user = new s.Entity('users', {
  roles: [role],
});
const userProfile = new s.Entity('userProfiles', {
  user: user,
});

const administrator = new s.Entity('administrators', {
  roles: [role],
});
const appKey = new s.Entity('appKeys');
const login = new s.Entity('logins');
const relationship = new s.Entity('relationships');
const passwordReset = new s.Entity('passwordResets', {
  user: user,
});
const importJob = new s.Entity('importJobs', {
  administrator: administrator,
});

// care guides
const CareGuideSegmentContent = new s.Entity('CareGuideSegmentContents');
const careGuideSegment = new s.Entity('careGuideSegments', {
  contents: [CareGuideSegmentContent],
});

const careGuideHelperTag = new s.Entity('careGuideHelperTags');

const careGuideMember = new s.Entity('careGuideMembers', {
  profile: userProfile,
});

const careGuideSectionField = new s.Entity('careGuideSectionFields');
const careGuideSection = new s.Entity('careGuideSections', {
  fields: [careGuideSectionField],
});
const careGuideTemplateSection = new s.Entity('careGuideTemplateSections', {
  section: careGuideSection,
});
const careGuideTemplate = new s.Entity('careGuideTemplates', {
  sections: [careGuideTemplateSection],
});

// accounts
const plan = new s.Entity('plans', {
  template: careGuideTemplate,
});

const account = new s.Entity('accounts', {
  plan: plan,
  owner: userProfile,
  template: careGuideTemplate,
});

const careGuideOwner = new s.Entity('careGuideOwners', {
  profile: userProfile,
  account: account,
});

const careGuide = new s.Entity('careGuides', {
  relationship: relationship,
  owner: careGuideOwner,
  account: account,
  segments: [careGuideSegment],
});

const careGuideInvitation = new s.Entity('careGuideInvitations', {
  care_guide: careGuide,
  profile: userProfile,
  inviter: userProfile,
});

const careGuideHelper = new s.Entity('careGuideHelpers', {
  care_guide: careGuide,
  profile: userProfile,
});

// partnerships

const partnership = new s.Entity('partnerships');
const userProfilesPartnership = new s.Entity('userProfilesPartnerships');

partnership.define({
  plan: plan,
  user_profiles_partnerships: [userProfilesPartnership],
});

userProfilesPartnership.define({
  profile: userProfile,
  partnership: partnership,
});

// media
const medium = new s.Entity('media', {
  care_guide: careGuide,
  owner: userProfile,
  partnership: partnership,
});

// journals
const journalPost = new s.Entity('journalPosts', {
  care_guide: careGuide,
  profile: userProfile,
  media: [medium],
});
const journalReply = new s.Entity('journalReplies', {
  profile: userProfile,
});
const journalTag = new s.Entity('journalTags');
const journalPartnerTag = new s.Entity('journalPartnerTags');
const journalPartnerTagsCareGuide = new s.Entity(
  'journalPartnerTagsCareGuides'
);

// messages
const messageRecipient = new s.Entity('messageRecipients', {
  profile: userProfile,
});
const message = new s.Entity('messages', {
  care_guide: careGuide,
  profile: userProfile,
  recipients: [messageRecipient],
});

// calendar
const calendarEventParticipant = new s.Entity('calendarEventParticipants', {
  profile: userProfile,
});

const calendarEvent = new s.Entity('calendarEvents');

calendarEvent.define({
  care_guide: careGuide,
  care_guides: [careGuide],
  partnership: partnership,
  profile: userProfile,
  participants: [calendarEventParticipant],
  parent_event: calendarEvent,
});

const calendarEventException = new s.Entity('calendarEventExceptions', {
  event: calendarEvent,
});

// resources
const resourceTag = new s.Entity('resourceTags');
const resourceServiceTag = new s.Entity('resourceServiceTags');
const resource = new s.Entity('resources', {
  care_guide: careGuide,
  care_guides: [careGuide],
  partnership: partnership,
  country: country,
  region: region,
  city: city,
  postal_code: postalCode,
  countries: [country],
  regions: [region],
  cities: [city],
  postal_codes: [postalCode],
  logo: medium,
});
const resourceNote = new s.Entity('resourceNotes', {
  profile: userProfile,
});

const registrationCode = new s.Entity('registrationCodes', {
  profile: userProfile,
  plan: plan,
  partnership: partnership,
});

// registratinos
const registration = new s.Entity('registrations', {
  invitation_code: careGuideInvitation,
  registration_code: registrationCode,
  relationship: relationship,
  terms_of_service: termsOfService,
  plan: plan,
  user: user,
});

const email = new s.Entity('emails');
const sms = new s.Entity('smses');

const adminPartnerClass = new s.Entity('adminPartnerClasses', {
  partnership: partnership,
});

const careGuidePartnerTag = new s.Entity('careGuidePartnerTags', {
  admin_partner_class: adminPartnerClass,
});

const careGuidePartnerTagsPartnership = new s.Entity(
  'careGuidePartnerTagsPartnerships'
);

const careGuideNote = new s.Entity('careGuideNotes', {
  care_guide: careGuide,
  profile: userProfile,
});

const careGuideDocument = new s.Entity('careGuideDocuments', {
  care_guide: careGuide,
  profile: userProfile,
});

const importTask = new s.Entity('importTasks', {
  care_guide: careGuide,
  administrator: administrator,
});

const alert = new s.Entity('alerts', {
  care_guide: careGuide,
  profile: userProfile,
  subject_profile: userProfile,
});

const partnershipFormAnswer = new s.Entity('partnershipFormAnswers', {
  profile: userProfile,
  care_guide: careGuide,
  partnership: partnership,
});

const dashboardSection = new s.Entity('dashboardSections', {
  partnership: partnership,
});

const recommendedUtility = new s.Entity('recommendedUtilities', {
  partnership: partnership,
  profile: userProfile,
});

const cognitoFormAnswer = new s.Entity('cognitoFormAnswers', {
  registration: registration,
});

const cognitoForm = new s.Entity('cognitoForms', {
  profile: userProfile,
  partnership: partnership,
  registration_code: registrationCode,
});

const partnershipTagRecipient = new s.Entity('partnershipTagsRecipients', {
  partnership: partnership,
});

const schema = {
  country: country,
  region: region,
  subregion: subregion,
  city: city,
  postalCode: postalCode,
  user: user,
  userProfile: userProfile,
  administrator: administrator,
  role: role,
  appKey: appKey,
  termsOfService: termsOfService,
  login: login,
  plan: plan,
  relationship: relationship,
  passwordReset: passwordReset,
  registrationCode: registrationCode,
  account: account,
  careGuide: careGuide,
  careGuideSegment: careGuideSegment,
  CareGuideSegmentContent: CareGuideSegmentContent,
  careGuideOwner: careGuideOwner,
  careGuideHelperTag: careGuideHelperTag,
  careGuideHelper: careGuideHelper,
  careGuideMember: careGuideMember,
  careGuideInvitation: careGuideInvitation,
  careGuideTemplate: careGuideTemplate,
  careGuideSection: careGuideSection,
  careGuideSectionField: careGuideSectionField,
  careGuideTemplateSection: careGuideTemplateSection,
  journalPost: journalPost,
  journalReply: journalReply,
  journalTag: journalTag,
  message: message,
  messageRecipient: messageRecipient,
  calendarEvent: calendarEvent,
  calendarEventParticipant: calendarEventParticipant,
  calendarEventException: calendarEventException,
  resourceTag: resourceTag,
  resourceServiceTag: resourceServiceTag,
  resource: resource,
  resourceNote: resourceNote,
  medium: medium,
  registration: registration,
  importJob: importJob,
  partnership: partnership,
  userProfilesPartnership: userProfilesPartnership,
  journalPartnerTag: journalPartnerTag,
  journalPartnerTagsCareGuide: journalPartnerTagsCareGuide,
  email: email,
  careGuidePartnerTag: careGuidePartnerTag,
  careGuidePartnerTagsPartnership: careGuidePartnerTagsPartnership,
  adminPartnerClass: adminPartnerClass,
  careGuideNote: careGuideNote,
  careGuideDocument: careGuideDocument,
  importTask: importTask,
  alert: alert,
  partnershipFormAnswer: partnershipFormAnswer,
  dashboardSection: dashboardSection,
  recommendedUtility: recommendedUtility,
  sms: sms,
  cognitoFormAnswer: cognitoFormAnswer,
  cognitoForm: cognitoForm,
  partnershipTagRecipient: partnershipTagRecipient,
};

export { schema };

Logger.log('silly', `schema loaded.`);
